import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import axios from "axios";
import { API_URL } from "../../../../config";
import "./UnshippedJobs.css";

// Shared implementation behind the two "outstanding work" circles:
//
//   Unshipped Jobs    — material still to be delivered (warehouse staff)
//   Non Shipped Labor — labor lines still to be entered (installation managers)
//
// Both read the same payload shape from their own endpoint and differ only in
// title, columns, and whether a Warehouse Shipments section applies. Keeping one
// component means a fix to the filter row or the failure state lands on both.
//
// Payload: result[0] = { missedDaysYtd, totalCount, alertCount, asOfDate,
//                        salesOrders[], warehouseShipments[] }
//
// "Installation Date" is BC's Requested_Delivery_Date (relabelled in the BC UI).
// The API already restricts the data to the previous day and older, so no date
// cutoff is re-applied here — the range picker below only narrows further.
export const ALERT_THRESHOLD = 10;

// Material: shipment number leads, so warehouse staff can scan it down the left
// edge of either table.
export const SALES_COLUMNS_WITH_SHIPMENT = [
  { key: "shipmentNo", label: "Shipment No." },
  { key: "jobNo", label: "Job No." },
  { key: "unitNumber", label: "Unit No." },
  { key: "installationDate", label: "Installation Date", type: "date" },
  { key: "storeName", label: "Store Name", type: "select" },
  { key: "costCenter", label: "Cost Center", type: "select" },
  { key: "jobDescription", label: "Job Description" },
];

// Labor: no shipment column — a labor line is never loaded onto a truck.
export const SALES_COLUMNS_NO_SHIPMENT = [
  { key: "jobNo", label: "Job No." },
  { key: "unitNumber", label: "Unit No." },
  { key: "installationDate", label: "Installation Date", type: "date" },
  { key: "storeName", label: "Store Name", type: "select" },
  { key: "costCenter", label: "Cost Center", type: "select" },
  { key: "jobDescription", label: "Job Description" },
];

export const SHIPMENT_COLUMNS = [
  { key: "shipmentNo", label: "Shipment No." },
  { key: "status", label: "Status", type: "select" },
  { key: "storeName", label: "Store Name", type: "select" },
  { key: "shipmentDate", label: "Shipment Date", type: "date" },
  { key: "assignedUserId", label: "Assigned User ID" },
  { key: "sortingMethod", label: "Sorting Method" },
];

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())}-${d.getFullYear()}`;
};

// Compare on the calendar date only — the API returns midnight timestamps, so
// including the time would make an end-of-range date exclude its own day.
const dayOf = (v) => {
  const d = new Date(v);
  return isNaN(d.getTime())
    ? null
    : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// One filter row beneath the headers: free text for most columns, a dropdown of
// the values actually present for low-cardinality ones, and a range picker for
// dates. Filtering is client-side because both sections are small (tens of rows).
export const FilterableTable = ({ columns, rows, emptyText }) => {
  const [text, setText] = useState({});
  const [range, setRange] = useState({});

  const options = useMemo(() => {
    const acc = {};
    columns
      .filter((c) => c.type === "select")
      .forEach((c) => {
        acc[c.key] = Array.from(
          new Set(rows.map((r) => r[c.key]).filter((v) => v))
        ).sort();
      });
    return acc;
  }, [columns, rows]);

  const filtered = useMemo(
    () =>
      rows.filter((row) =>
        columns.every((c) => {
          if (c.type === "date") {
            const [from, to] = range[c.key] || [null, null];
            if (!from && !to) return true;
            const v = dayOf(row[c.key]);
            if (v === null) return false;
            if (from && v < dayOf(from)) return false;
            if (to && v > dayOf(to)) return false;
            return true;
          }
          const q = (text[c.key] || "").trim().toLowerCase();
          if (!q) return true;
          return String(row[c.key] ?? "")
            .toLowerCase()
            .includes(q);
        })
      ),
    [rows, columns, text, range]
  );

  return (
    <>
      <div className="uj-result-count">
        Showing {filtered.length} of {rows.length}
      </div>
      <div className="uj-modal-scroll">
        <Table striped bordered hover size="sm" className="uj-modal-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
            <tr className="uj-filter-row">
              {columns.map((c) => (
                <th key={c.key}>
                  {c.type === "date" ? (
                    <DatePicker
                      selectsRange
                      startDate={(range[c.key] || [])[0]}
                      endDate={(range[c.key] || [])[1]}
                      onChange={(upd) =>
                        setRange((p) => ({ ...p, [c.key]: upd }))
                      }
                      dateFormat="MM-dd-yyyy"
                      placeholderText="MM-DD-YYYY - MM-DD-YYYY"
                      className="form-control form-control-sm"
                      isClearable
                    />
                  ) : c.type === "select" ? (
                    <Form.Select
                      size="sm"
                      value={text[c.key] || ""}
                      onChange={(e) =>
                        setText((p) => ({ ...p, [c.key]: e.target.value }))
                      }
                    >
                      <option value="">Select</option>
                      {(options[c.key] || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      size="sm"
                      value={text[c.key] || ""}
                      onChange={(e) =>
                        setText((p) => ({ ...p, [c.key]: e.target.value }))
                      }
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="uj-modal-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              filtered.map((row, i) => (
                <tr key={row.orderNo || row.shipmentNo || i}>
                  {columns.map((c) => (
                    <td key={c.key}>
                      {c.type === "date" ? fmtDate(row[c.key]) : row[c.key] || ""}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

/**
 * @param token          bearer token from auth state
 * @param endpoint       Dashboard controller action name
 * @param title          circle label and modal title
 * @param icon           emoji shown in the alert state
 * @param salesColumns   column config for the sales-order section
 * @param salesSectionTitle heading for that section
 * @param showShipments  render the Warehouse Shipments section
 */
const UnshippedWorkWidget = ({
  token,
  endpoint,
  title,
  icon,
  salesColumns,
  salesSectionTitle,
  showShipments,
}) => {
  // null = not loaded / failed. Distinguished from 0 via `status` so a failed
  // request never renders as a calm green zero.
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!token) return;
    // Guard against setting state after the widget unmounts (the dashboard can
    // re-render/unmount widgets while this request is still in flight).
    let active = true;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setData(rows[0] || null);
        setStatus(rows[0] ? "ok" : "error");
      })
      .catch((err) => {
        if (!active) return;
        console.warn(`Could not load ${title}:`, err.message);
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [token, endpoint, title]);

  const loaded = status === "ok" && data;
  const salesOrders = (loaded && data.salesOrders) || [];
  const shipments = (loaded && data.warehouseShipments) || [];
  const totalCount = loaded ? Number(data.totalCount) || 0 : null;
  const alertCount = loaded ? Number(data.alertCount ?? data.totalCount) || 0 : null;
  const isAlert = loaded && alertCount > ALERT_THRESHOLD;
  // Days this year that ended with something still outstanding. Recorded forward
  // from the day the daily check first ran, so it reads 0 on a fresh install.
  const missedDays = loaded ? Number(data.missedDaysYtd) || 0 : null;

  const stateClass = !loaded ? "uj-unknown" : isAlert ? "uj-alert" : "uj-calm";

  return (
    <div className="uj-widget">
      <button
        type="button"
        className={`uj-button ${stateClass}`}
        onClick={() => loaded && setShowDetails(true)}
        disabled={!loaded}
        title={loaded ? `View ${title.toLowerCase()}` : `${title} is unavailable`}
      >
        {isAlert && (
          <span className="uj-icon" role="img" aria-label="alert">
            {icon}
          </span>
        )}
        <span className="uj-label">{title}</span>
        {/* Headline is days missed YTD; the outstanding count sits beneath it. */}
        <span className="uj-count">
          {status === "loading" ? "…" : loaded ? missedDays : "—"}
        </span>
        <span className="uj-count-caption">
          {missedDays === 1 ? "day missed YTD" : "days missed YTD"}
        </span>
        <span className="uj-subcount">
          {status === "loading" ? "" : loaded ? `${totalCount} open` : ""}
        </span>
      </button>

      {loaded && data.asOfDate && (
        <div className="uj-subnote">
          Install date on or before {fmtDate(data.asOfDate)}
        </div>
      )}

      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="xl"
        centered
        scrollable
        dialogClassName="uj-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <section className="uj-section">
            <h5 className="uj-section-title">
              {salesSectionTitle} ({salesOrders.length})
            </h5>
            <FilterableTable
              columns={salesColumns}
              rows={salesOrders}
              emptyText="No matching sales orders."
            />
          </section>

          {showShipments && (
            <section className="uj-section">
              <h5 className="uj-section-title">
                Warehouse Shipments ({shipments.length})
              </h5>
              <FilterableTable
                columns={SHIPMENT_COLUMNS}
                rows={shipments}
                emptyText="No matching warehouse shipments."
              />
            </section>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default UnshippedWorkWidget;
